import React, { useState, useEffect } from 'react';
import { useFilters } from '../../context/FiltersContext';
import ScopeBadge from '../ScopeBadge';
import MunicipalitySilhouette from '../MunicipalitySilhouette';

interface HeroBannerProps {
  kommunName: string | null;
  selectedYear: number;
}

export const HeroBanner: React.FC<HeroBannerProps> = ({
  kommunName,
  selectedYear
}) => {
  const { value: globalFilters } = useFilters();
  const [countyName, setCountyName] = useState<string>('...');

  useEffect(() => {
    if (!globalFilters.kommunId) return;
    const code = String(globalFilters.kommunId).padStart(4, '0').substring(0, 2);

    // Hardcoded county mapping since county_codes table doesn't exist in WASM
    const countyNames: Record<string, string> = {
      '01': 'Stockholms län', '03': 'Uppsala län', '04': 'Södermanlands län',
      '05': 'Östergötlands län', '06': 'Jönköpings län', '07': 'Kronobergs län',
      '08': 'Kalmar län', '09': 'Gotlands län', '10': 'Blekinge län',
      '12': 'Skåne län', '13': 'Hallands län', '14': 'Västra Götalands län',
      '17': 'Värmlands län', '18': 'Örebro län', '19': 'Västmanlands län',
      '20': 'Dalarnas län', '21': 'Gävleborgs län', '22': 'Västernorrlands län',
      '23': 'Jämtlands län', '24': 'Västerbottens län', '25': 'Norrbottens län'
    };

    setCountyName(countyNames[code] || '...');
  }, [globalFilters.kommunId]);

  return (
    <div className="relative overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm mb-8 transition-all duration-500">
      {/* Background decoration - subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/30 via-transparent to-transparent dark:from-emerald-950/10 pointer-events-none" />
      
      <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] lg:grid-cols-[1fr_420px]">
        
        {/* LEFT COLUMN: Content */}
        <div className="p-6 md:p-10 lg:p-12 relative z-10">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 dark:text-white">
                {kommunName || 'Laddar...'}
              </h1>
              <ScopeBadge variant="slate" className="h-7 text-sm px-3">{selectedYear}</ScopeBadge>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-1">
              {globalFilters.huvudmanId && (
                <ScopeBadge variant="outline" className="text-[10px] uppercase tracking-widest font-black py-1">
                  {globalFilters.huvudmanId === 'kommunal' ? 'Kommunala skolor' : 'Fristående skolor'}
                </ScopeBadge>
              )}
              {globalFilters.skolaId && (
                <ScopeBadge variant="emerald" className="text-[10px] uppercase tracking-widest font-black py-1" icon={<span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />}>
                  Enskild skola
                </ScopeBadge>
              )}
              <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-[0.1em] ml-1">
                <span>Utbildningsanalys</span>
                <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-700" />
                <span>Nyckeltal</span>
              </div>
            </div>

            {/* Micro-insights / Status Area */}
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-4 border-t border-slate-100 dark:border-slate-800/50 pt-6">
              <div className="flex flex-col gap-1">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Län</span>
                <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{countyName}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Kategori</span>
                <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                  Kommun
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Status</span>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Aktiv analys</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Silhouette Art Stage - the stage owns its height */}
        <div className="relative h-[200px] md:h-full min-h-[300px] border-t md:border-t-0 md:border-l border-slate-100 dark:border-slate-800 bg-slate-50/20 dark:bg-slate-950/10 overflow-hidden">
          {/* MunicipalitySilhouette renders SVG with absolute inset-0 directly */}
          {globalFilters.kommunId && (
            <MunicipalitySilhouette 
              kommunId={globalFilters.kommunId} 
              variant="hero"
            />
          )}
          
          {/* Subtle Glass Overlay Effect */}
          <div className="absolute inset-0 bg-gradient-to-tr from-white/10 via-transparent to-transparent dark:from-white/5 pointer-events-none" />
        </div>
      </div>
    </div>
  );
};

export default HeroBanner;

